import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Loader2, Search, Settings, GitBranch, X, Filter } from 'lucide-react';
import { approvalAPI } from '../services/api';
import { formatDateTime } from '../utils/timezone';
import { toast } from 'sonner';
import Paginator from '../components/ui/Paginator';

// ─── Priority labels & colours ────────────────────────────────────────────────
const SR_URGENCY = {
  asap:      { label: 'ASAP',      cls: 'bg-rose-100 text-rose-800',   sort: 0 },
  this_week: { label: 'This Week', cls: 'bg-amber-100 text-amber-800', sort: 1 },
  standard:  { label: 'Standard',  cls: 'bg-blue-100 text-blue-800',   sort: 2 },
  no_rush:   { label: 'No Rush',   cls: 'bg-slate-100 text-slate-700', sort: 3 },
};
const CR_PRIORITY = {
  immediate: { label: 'Immediate', cls: 'bg-rose-100 text-rose-800',   sort: 0 },
  high:      { label: 'High',      cls: 'bg-amber-100 text-amber-800', sort: 1 },
  medium:    { label: 'Medium',    cls: 'bg-blue-100 text-blue-800',   sort: 2 },
  low:       { label: 'Low',       cls: 'bg-slate-100 text-slate-700', sort: 3 },
};

function SRBadge({ urgency }) {
  const m = SR_URGENCY[urgency] || SR_URGENCY.standard;
  return <span className={`${m.cls} px-2 py-0.5 rounded-full text-xs font-semibold`}>{m.label}</span>;
}
function CRBadge({ priority }) {
  const m = CR_PRIORITY[priority] || CR_PRIORITY.medium;
  return <span className={`${m.cls} px-2 py-0.5 rounded-full text-xs font-semibold`}>{m.label}</span>;
}

// ─── Single approval row ───────────────────────────────────────────────────────
function ApprovalRow({ a, onApprove, onReject, navigate, type }) {
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors" data-testid={`approval-${a.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-sm font-bold text-slate-800 dark:text-white">{a.ticket_number}</span>
          <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded font-bold">L{a.level}</span>
        </div>
      </td>
      <td className="px-4 py-3 max-w-xs">
        <p className="text-sm text-slate-700 dark:text-slate-200 truncate" title={a.ticket_title}>{a.ticket_title}</p>
        {a.application_name && <p className="text-xs text-slate-400 truncate">{a.application_name}</p>}
      </td>
      <td className="px-4 py-3">
        {type === 'sr' ? <SRBadge urgency={a.urgency_level} /> : <CRBadge priority={a.cr_priority} />}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">{a.reporter_name || '—'}</td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {a.created_at ? formatDateTime(a.created_at) : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => navigate(`/ticket/${a.ticket_type}/${a.ticket_id}`)}
            className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2.5 py-1.5 rounded-lg text-xs hover:bg-slate-200 dark:hover:bg-slate-600">
            View
          </button>
          <button onClick={() => onApprove(a)}
            className="bg-emerald-500 text-white px-2.5 py-1.5 rounded-lg text-xs hover:bg-emerald-600 flex items-center gap-1" data-testid="approve-btn">
            <CheckCircle className="w-3.5 h-3.5" /> Approve
          </button>
          <button onClick={() => onReject(a)}
            className="bg-rose-500 text-white px-2.5 py-1.5 rounded-lg text-xs hover:bg-rose-600 flex items-center gap-1" data-testid="reject-btn">
            <XCircle className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Section (SR or CR) with search + sort ─────────────────────────────────────
function ApprovalSection({ items, type, onApprove, onReject, navigate }) {
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('created_at');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const SORT_OPTIONS = [
    { value: 'created_at',    label: 'Date (Oldest first)' },
    { value: 'created_at_d',  label: 'Date (Newest first)' },
    { value: 'priority',      label: 'Priority (High → Low)' },
    { value: 'ticket_number', label: 'Ticket # (A → Z)' },
  ];

  const filtered = useMemo(() => {
    let list = [...items];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        (a.ticket_number || '').toLowerCase().includes(q) ||
        (a.ticket_title  || '').toLowerCase().includes(q) ||
        (a.reporter_name || '').toLowerCase().includes(q) ||
        (a.application_name || '').toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      switch (sortOption) {
        case 'created_at':   return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'created_at_d': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'priority':     return (a.priority_sort ?? 99) - (b.priority_sort ?? 99);
        case 'ticket_number': return (a.ticket_number || '').localeCompare(b.ticket_number || '');
        default: return 0;
      }
    });
    return list;
  }, [items, search, sortOption]);

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const thCls = 'px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide bg-slate-50 dark:bg-slate-900';
  const Icon = type === 'sr' ? Settings : GitBranch;
  const label = type === 'sr' ? 'Service Requests' : 'Change Requests';
  const clr = type === 'sr' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400';
  const bgBadge = type === 'sr' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden mb-6">
      {/* Section header */}
      <div className="flex items-center justify-between px-5 py-4 border-b dark:border-slate-700 flex-wrap gap-3">
        <h3 className={`font-semibold flex items-center gap-2 ${clr}`}>
          <Icon className="w-4 h-4" />
          {label}
          <span className={`${bgBadge} text-xs px-2 py-0.5 rounded-full font-bold`}>{filtered.length}</span>
        </h3>
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input type="text" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder={`Search ${type.toUpperCase()} tickets...`}
              className="pl-8 pr-7 py-2 border dark:border-slate-700 rounded-lg text-xs bg-white dark:bg-slate-900 dark:text-white w-52 focus:outline-none focus:ring-1 focus:ring-sky-500"
              data-testid={`search-${type}`}
            />
            {search && (
              <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <select value={sortOption} onChange={e => { setSortOption(e.target.value); setPage(1); }}
              className="border dark:border-slate-700 rounded-lg px-2.5 py-2 text-xs bg-white dark:bg-slate-900 dark:text-white focus:outline-none"
              data-testid={`sort-${type}`}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {paged.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">{search ? `No ${type.toUpperCase()} matching "${search}"` : `No pending ${label.toLowerCase()}`}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={thCls}>Ticket #</th>
                  <th className={thCls}>Title</th>
                  <th className={thCls}>{type === 'sr' ? 'Urgency' : 'Priority'}</th>
                  <th className={thCls}>Reporter</th>
                  <th className={thCls}>Created</th>
                  <th className={thCls}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {paged.map(a => (
                  <ApprovalRow key={a.id} a={a} type={type} onApprove={onApprove} onReject={onReject} navigate={navigate} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t dark:border-slate-700">
            <Paginator total={filtered.length} page={page} pageSize={pageSize}
              onPage={p => setPage(p)} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function MyApprovalsPage() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await approvalAPI.listPending();
      setPending(res.data?.results || res.data || []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, []);

  const srItems = pending.filter(a => a.ticket_type === 'sr');
  const crItems = pending.filter(a => a.ticket_type === 'cr');

  const handleApprove = (approval) => { setActionModal({ approval, action: 'approve' }); setComments(''); };
  const handleReject  = (approval) => { setActionModal({ approval, action: 'reject'  }); setComments(''); };

  const handleAction = async () => {
    if (!actionModal) return;
    setSubmitting(true);
    try {
      if (actionModal.action === 'approve') {
        await approvalAPI.approve(actionModal.approval.id, comments);
        toast.success(`${actionModal.approval.ticket_number} approved`);
      } else {
        if (!comments.trim()) { toast.error('Rejection requires a comment'); setSubmitting(false); return; }
        await approvalAPI.reject(actionModal.approval.id, comments);
        toast.success(`${actionModal.approval.ticket_number} rejected`);
      }
      setActionModal(null); setComments(''); fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally { setSubmitting(false); }
  };

  const tabs = [
    { id: 'all', label: 'All',              count: pending.length  },
    { id: 'sr',  label: 'Service Requests', count: srItems.length, Icon: Settings  },
    { id: 'cr',  label: 'Change Requests',  count: crItems.length, Icon: GitBranch },
  ];

  return (
    <div data-testid="my-approvals-page">
      {/* Approve/Reject Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-1">
              {actionModal.action === 'approve'
                ? <CheckCircle className="w-5 h-5 text-emerald-500" />
                : <XCircle className="w-5 h-5 text-rose-500" />}
              <h3 className="font-bold text-lg dark:text-white">
                {actionModal.action === 'approve' ? 'Approve' : 'Reject'} — {actionModal.approval.ticket_number}
              </h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{actionModal.approval.ticket_title}</p>
            {actionModal.approval.application_name && (
              <p className="text-xs text-slate-400 mb-4">{actionModal.approval.application_name}</p>
            )}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                Comments {actionModal.action === 'reject' ? <span className="text-rose-500">*</span> : <span className="text-slate-400">(optional)</span>}
              </label>
              <textarea value={comments} onChange={e => setComments(e.target.value)} rows={3}
                placeholder={actionModal.action === 'reject' ? 'Reason for rejection (required)...' : 'Add comments...'}
                className="w-full border dark:border-slate-700 rounded-xl px-4 py-3 text-sm dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-sky-500 outline-none resize-none"
                autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setActionModal(null)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl text-sm">Cancel</button>
              <button onClick={handleAction}
                disabled={submitting || (actionModal.action === 'reject' && !comments.trim())}
                className={`px-5 py-2 rounded-xl text-sm text-white flex items-center gap-2 disabled:opacity-50 ${
                  actionModal.action === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'
                }`}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {actionModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <Clock className="w-6 h-6 text-amber-500" /> My Pending Approvals
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {loading ? 'Loading...' : `${pending.length} ticket${pending.length !== 1 ? 's' : ''} awaiting your action`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : pending.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-16 text-center">
          <CheckCircle className="w-14 h-14 mx-auto text-emerald-300 mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-semibold text-lg">All caught up!</p>
          <p className="text-sm text-slate-400 mt-1">No pending approvals at this time.</p>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 border dark:border-slate-700 p-1 rounded-xl mb-6 w-fit">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === t.id
                    ? 'bg-white dark:bg-slate-700 text-sky-700 dark:text-sky-400 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}>
                {t.Icon && <t.Icon className="w-3.5 h-3.5" />}
                {t.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === t.id
                    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400'
                    : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                }`}>{t.count}</span>
              </button>
            ))}
          </div>

          {/* SR Section */}
          {(activeTab === 'all' || activeTab === 'sr') && srItems.length > 0 && (
            <ApprovalSection items={srItems} type="sr" onApprove={handleApprove} onReject={handleReject} navigate={navigate} />
          )}
          {activeTab === 'sr' && srItems.length === 0 && (
            <div className="text-center py-10 text-slate-400 bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700">
              No pending Service Requests
            </div>
          )}

          {/* CR Section */}
          {(activeTab === 'all' || activeTab === 'cr') && crItems.length > 0 && (
            <ApprovalSection items={crItems} type="cr" onApprove={handleApprove} onReject={handleReject} navigate={navigate} />
          )}
          {activeTab === 'cr' && crItems.length === 0 && (
            <div className="text-center py-10 text-slate-400 bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700">
              No pending Change Requests
            </div>
          )}
        </>
      )}
    </div>
  );
}
